import { useQuery } from '@tanstack/react-query';
import { Fragment, type ReactNode } from 'react';

import { entityApprovalStatus } from '@/api/entity/types';
import { _getUserEntities, useGetUserProfile } from '@/api/user-dashboard/queries';
import LogoMain from '@/assets/svg/logos/logo-main.svg';
import { useCommunicationsUnreadCount } from '@/global-hooks/use-communications-unread-count';
import { PagePaths } from '@/routes/page-paths';
import { useLayoutSlice, useSelectedUserConfigSlice } from '@/stores/selectors';

import Separator from '../Separator';
import {
  BottomSection,
  LogoLink,
  LogoSection,
  LogoSwap,
  MenuSection,
  SidebarMenu,
  StyledSidebar,
  TopSection,
} from './Sidebar.styled';
import { SidebarItem } from './sidebar-item';
import { useGetSidebarBottomLinks } from './use-get-sidebar-bottom-links';
import { useGetSidebarLinks } from './use-get-sidebar-links';

type SidebarProps = {
  /** Rendered under the bottom links, only while the sidebar is expanded. */
  bottomSlot?: ReactNode;
};

export const Sidebar = ({ bottomSlot }: SidebarProps) => {
  const { selectedEntityId, selectedUserRole } = useSelectedUserConfigSlice();
  const { isSidebarCollapsed } = useLayoutSlice();

  const { data: entitiesData } = useQuery(_getUserEntities());
  const selectedEntity =
    selectedUserRole === 'entity'
      ? entitiesData?.entities.find((e) => e.entityId === selectedEntityId)
      : undefined;
  const isEntityRejected =
    selectedEntity?.entityApprovalStatus === entityApprovalStatus.Rejected ||
    selectedEntity?.entityApprovalStatus === entityApprovalStatus.Blocked;

  const { user } = useGetUserProfile();

  const links = useGetSidebarLinks({
    role: selectedUserRole,
    entityId: selectedEntityId || undefined,
    userId: user?.id,
    hasSubmittedQuestionnaire: user?.hasSubmittedQuestionnaire,
    isBlocked: user?.isBlocked,
    blockReason: user?.blockReason,
    kycStatus: user?.kycStatus,
    isEntityRejected,
    isNonAccredited: user?.isNonAccredited,
  });

  const bottomLinks = useGetSidebarBottomLinks();

  const totalUnread = useCommunicationsUnreadCount();

  return (
    <StyledSidebar $collapsed={isSidebarCollapsed}>
      <TopSection>
        <LogoSection>
          <LogoLink to={PagePaths.allFunds} aria-label="Gamma Prime">
            <LogoSwap $collapsed={isSidebarCollapsed}>
              <LogoMain />
            </LogoSwap>
          </LogoLink>
        </LogoSection>

        <MenuSection aria-label="Main navigation">
          {links.map((linkGroup, idx) => (
            <Fragment key={`${idx}_${linkGroup.length}`}>
              {idx !== 0 && <Separator spacing="spacious" tone="borderSubtle" />}

              <SidebarMenu>
                {linkGroup.map((link) => (
                  <SidebarItem
                    key={link.text}
                    {...link}
                    collapsed={isSidebarCollapsed}
                    unreadCount={link.href === PagePaths.communications ? totalUnread : undefined}
                  />
                ))}
              </SidebarMenu>
            </Fragment>
          ))}
        </MenuSection>
      </TopSection>

      <BottomSection>
        <SidebarMenu>
          {bottomLinks.map((link) => (
            <SidebarItem key={link.text} {...link} collapsed={isSidebarCollapsed} />
          ))}
        </SidebarMenu>
        {!isSidebarCollapsed && bottomSlot}
      </BottomSection>
    </StyledSidebar>
  );
};
